import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { AuctionTimer, ScheduledTimer } from "../components/ProductCard";
import ProfileCompletionModal from "../components/ProfileCompletionModal";
import QuickOfferPopup from "../components/QuickOfferPopup";
import { WhatsAppBadge } from "../components/WhatsAppBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "../components/ui/drawer";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth, useWS, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { handleImgError } from "../lib/imageFallback";
import { Heart, MessageCircle, ArrowLeft, Gavel, ShoppingCart, Minus, Plus, Clock, CreditCard, Star, Zap, Tag, MessageSquare, Users, Check, X, CheckCircle, Shield, AlertTriangle, Gift, Trash2, Pencil, Share2 } from "lucide-react";
import { ProductCard } from "../components/ProductCard";
import { shareProductWhatsApp } from "../lib/share";

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const ws = useWS();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommended, setRecommended] = useState([]);
  const [bids, setBids] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [sellerRating, setSellerRating] = useState({ avg_rating: 0, review_count: 0, sold_count: 0 });
  const [bidAmount, setBidAmount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [liveUpdateHighlight, setLiveUpdateHighlight] = useState(false);
  
  // Image gallery state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  
  // Comprar Ahora and Mejor Oferta states
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const [priceAccepted, setPriceAccepted] = useState(false);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [offerAmount, setOfferAmount] = useState(0);
  const [offerCount, setOfferCount] = useState({ count: 0, remaining: 3, has_pending_counter: false, last_counter_amount: null, latest_offer_id: null });
  const [offerTermsAccepted, setOfferTermsAccepted] = useState(false);
  
  // Quick offer popup mode: "offer" or "direct_purchase"
  const [quickOfferMode, setQuickOfferMode] = useState("offer");
  
  // Bid confirmation states
  const [bidConfirmationOpen, setBidConfirmationOpen] = useState(false);
  const [bidTermsAccepted, setBidTermsAccepted] = useState(false);
  const [userHasBidBefore, setUserHasBidBefore] = useState(false);
  const [bidTermsOpen, setBidTermsOpen] = useState(false);
  const [bidSuggestions, setBidSuggestions] = useState([]);
  const [bidCount, setBidCount] = useState(0);
  
  // User's accepted offer state
  const [userAcceptedOffer, setUserAcceptedOffer] = useState(null);
  
  // User's purchase intent state (reserved/pending/accepted)
  const [userPurchaseIntent, setUserPurchaseIntent] = useState(null);
  
  // Profile completion state
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileCompleted, setProfileCompleted] = useState(true); // Default to true, will be updated
  const [profileChecked, setProfileChecked] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'buy', 'bid', 'offer'
  const [paymentInfoOpen, setPaymentInfoOpen] = useState(false); // Payment info modal
  
  // Quick offer popup for non-logged users
  const [quickOfferOpen, setQuickOfferOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
    fetchRecommended();
  }, [productId]);

  useEffect(() => {
    if (user && token && productId) {
      axios.post(`${API}/products/${productId}/track-view`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
  }, [productId, user, token]);

  useEffect(() => {
    if (product && product.sale_type === "auction") {
      fetchBids();
      fetchBidSuggestions();
      setBidAmount((product.current_bid || product.price) + 10);
    }
    if (product) {
      fetchSellerRating(product.user_id);
      setOfferAmount(Math.floor(product.price * 0.9));
    }
    if (user && token) {
      checkLiked();
      checkProfileCompleted();
      if (product?.sale_type === "direct") {
        fetchOfferCount();
      }
      if (product?.sale_type === "auction") {
        checkUserBidHistory();
      }
      // Check if user has an accepted offer for this product
      checkUserAcceptedOffer();
      // Check if user has an active purchase intent for this product
      checkUserPurchaseIntent();
    }
  }, [product, user, token]);

  useEffect(() => {
    if (ws && productId && product?.sale_type === "auction") {
      ws.subscribeToProduct(productId);
    }
  }, [ws, productId, product?.sale_type]);

  useEffect(() => {
    if (ws?.bidUpdates?.[productId]) {
      const newBid = ws.bidUpdates[productId];
      setProduct(prev => prev ? { ...prev, current_bid: newBid.amount } : prev);
      setBids(prev => [{ bid_id: newBid.bid_id, user_name: newBid.user_name, amount: newBid.amount, created_at: newBid.created_at }, ...prev]);
      setBidAmount(newBid.amount + 10);
      setLiveUpdateHighlight(true);
      setTimeout(() => setLiveUpdateHighlight(false), 2000);
      ws.clearBidUpdate(productId);
    }
  }, [ws?.bidUpdates, productId]);

  const checkProfileCompleted = async () => {
    if (profileChecked) return; // Don't check twice
    try {
      const response = await axios.get(`${API}/auth/me/full`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      const completed = response.data.profile_completed === true;
      setProfileCompleted(completed);
      setProfileChecked(true);
    } catch (error) {
      console.error("Error checking profile:", error);
      // On error, assume profile is complete to not block user
      setProfileCompleted(true);
      setProfileChecked(true);
    }
  };

  const checkUserBidHistory = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}/bids`);
      const userBid = response.data.find(bid => bid.user_id === user?.user_id);
      setUserHasBidBefore(!!userBid);
    } catch (error) {
      console.error("Error checking bid history:", error);
    }
  };

  const requireProfileCompletion = (action) => {
    // If profile not checked yet, check it first
    if (!profileChecked) {
      checkProfileCompleted();
    }
    // If profile is not completed, show the modal
    if (!profileCompleted) {
      setPendingAction(action);
      setProfileModalOpen(true);
      return false;
    }
    return true;
  };

  const handleProfileComplete = () => {
    setProfileCompleted(true);
    setProfileModalOpen(false);
    // Execute pending action
    if (pendingAction === 'buy') {
      setBuyDialogOpen(true);
    } else if (pendingAction === 'bid') {
      if (userHasBidBefore) {
        setBidDialogOpen(true);
      } else {
        setBidConfirmationOpen(true);
      }
    } else if (pendingAction === 'offer') {
      setOfferDialogOpen(true);
    }
    setPendingAction(null);
  };

  const checkUserAcceptedOffer = async () => {
    if (!user || !token || !productId) return;
    try {
      const response = await axios.get(`${API}/offers/my-offers`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      const acceptedOffer = response.data.find(
        o => o.product_id === productId && o.status === 'accepted'
      );
      setUserAcceptedOffer(acceptedOffer);
    } catch (error) {
      console.error("Error checking accepted offer:", error);
    }
  };

  const checkUserPurchaseIntent = async () => {
    if (!user || !token || !productId) return;
    try {
      const response = await axios.get(`${API}/purchase-intents/my-intents`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      const activeIntent = response.data.find(
        intent => intent.product_id === productId && ['pending', 'reserved', 'accepted'].includes(intent.status)
      );
      setUserPurchaseIntent(activeIntent);
    } catch (error) {
      console.error("Error checking purchase intent:", error);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      toast.error("Producto no encontrado");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommended = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      const active = response.data.filter(
        (p) => p.product_id !== productId && (p.status === "active" || !p.status)
      );
      const auctions = active.filter((p) => p.sale_type === "auction");
      const direct = active.filter((p) => p.sale_type !== "auction");
      const mixed = [];
      const max = Math.max(auctions.length, direct.length);
      for (let i = 0; i < max && mixed.length < 6; i++) {
        if (auctions[i]) mixed.push(auctions[i]);
        if (direct[i] && mixed.length < 6) mixed.push(direct[i]);
      }
      setRecommended(mixed);
    } catch (error) {
      console.error("Error fetching recommended:", error);
    }
  };

  const fetchBids = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}/bids`);
      setBids(response.data);
      setBidCount(response.data.length);
    } catch (error) {
      console.error("Error fetching bids:", error);
    }
  };

  const fetchBidSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}/bid-suggestions`);
      setBidSuggestions(response.data.suggestions || []);
      setBidCount(response.data.bid_count || 0);
    } catch (error) {
      console.error("Error fetching bid suggestions:", error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/product/${productId}`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta reseña?")) return;
    
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setReviews(prev => prev.filter(r => r.review_id !== reviewId));
      toast.success("Reseña eliminada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar reseña");
    }
  };

  const fetchSellerRating = async (sellerId) => {
    try {
      const response = await axios.get(`${API}/users/${sellerId}/rating`);
      setSellerRating(response.data);
    } catch (error) {
      console.error("Error fetching seller rating:", error);
    }
  };

  const fetchOfferCount = async () => {
    if (!token) return;
    try {
      // Add timestamp to prevent browser caching
      const response = await axios.get(`${API}/offers/count/${productId}?t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        },
        withCredentials: true
      });
      setOfferCount(response.data);
      console.log("Offer count updated:", response.data);
    } catch (error) {
      console.error("Error fetching offer count:", error);
    }
  };

  const checkLiked = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}/liked`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setLiked(response.data.liked);
    } catch (error) {}
  };

  const handleLike = async () => {
    if (!user) {
      toast.error("Inicia sesión para dar like");
      navigate("/login");
      return;
    }
    try {
      const response = await axios.post(`${API}/products/${productId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setLiked(response.data.liked);
      setProduct(prev => ({ ...prev, likes_count: response.data.liked ? prev.likes_count + 1 : prev.likes_count - 1 }));
    } catch (error) {
      toast.error("Error al dar like");
    }
  };

  const handleBuyNowClick = () => {
    if (!user) {
      // Show quick offer popup in direct_purchase mode for non-logged users
      setQuickOfferMode("direct_purchase");
      setQuickOfferOpen(true);
      return;
    }
    if (requireProfileCompletion('buy')) {
      setBuyDialogOpen(true);
    }
  };

  const handleBidClick = () => {
    if (!user) {
      // Show quick offer popup in offer mode for non-logged users
      setQuickOfferMode("offer");
      setQuickOfferOpen(true);
      return;
    }
    if (!requireProfileCompletion('bid')) return;
    
    if (userHasBidBefore) {
      setBidDialogOpen(true);
    } else {
      setBidConfirmationOpen(true);
    }
  };

  const handleOfferClick = () => {
    if (!user) {
      // Show quick offer popup in offer mode for non-logged users
      setQuickOfferMode("offer");
      setQuickOfferOpen(true);
      return;
    }
    if (requireProfileCompletion('offer')) {
      setOfferDialogOpen(true);
    }
  };

  const handleQuickOfferSuccess = (result) => {
    // Refresh product data after quick offer
    fetchProduct();
    if (product?.sale_type === "auction") {
      fetchBids();
    }
    // Page will auto-update because user is now logged in
  };

  const handleBidTermsAccepted = () => {
    setBidConfirmationOpen(false);
    setUserHasBidBefore(true);
    setBidDialogOpen(true);
  };

  const handleBid = async () => {
    if (bidAmount <= (product.current_bid || product.price)) {
      toast.error(`La oferta debe ser mayor a $${product.current_bid || product.price}`);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/products/${productId}/bids`, { amount: bidAmount }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("¡Oferta realizada con éxito!");
      setBidDialogOpen(false);
      fetchProduct();
      fetchBids();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al realizar oferta");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMessage = () => {
    if (!user) {
      toast.error("Inicia sesión para enviar mensaje");
      navigate("/login");
      return;
    }
    // Navigate to chat with return info
    navigate(`/chat/${product.user_id}`, { state: { from: 'product', productId: productId } });
  };

  const handleSendOffer = async () => {
    if (offerAmount <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    if (!offerTermsAccepted) {
      toast.error("Debes aceptar los términos para enviar tu oferta");
      return;
    }
    setSubmitting(true);
    try {
      // If responding to a counter offer, use the respond endpoint
      if (offerCount.has_pending_counter && offerCount.latest_offer_id) {
        await axios.post(`${API}/offers/${offerCount.latest_offer_id}/respond-with-offer`, 
          { product_id: productId, amount: offerAmount }, 
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }
        );
        toast.success(`¡Nueva oferta enviada! Te quedan ${offerCount.remaining - 1} ofertas.`);
      } else {
        await axios.post(`${API}/offers`, { product_id: productId, amount: offerAmount }, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        toast.success("¡Oferta enviada! El vendedor la revisará pronto.");
      }
      setOfferDialogOpen(false);
      setOfferTermsAccepted(false);
      // Force immediate update of offer count
      setTimeout(() => {
        fetchOfferCount();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar oferta");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptCounterOffer = async () => {
    if (!offerCount.latest_offer_id) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/offers/${offerCount.latest_offer_id}/accept-counter`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("¡Contraoferta aceptada! El producto está reservado para ti.");
      setOfferDialogOpen(false);
      navigate("/my-purchases");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aceptar contraoferta");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectCounterOffer = async () => {
    if (!offerCount.latest_offer_id) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/offers/${offerCount.latest_offer_id}/reject-counter`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Contraoferta rechazada.");
      setOfferDialogOpen(false);
      fetchOfferCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar contraoferta");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Ingresa un código de cupón");
      return;
    }
    setCouponLoading(true);
    try {
      const response = await axios.post(`${API}/products/${productId}/validate-coupon?coupon_code=${encodeURIComponent(couponCode)}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCouponApplied(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Código de cupón inválido");
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleMessageBuyer = () => {
    const buyerId = product.buyer_id || product.winner_id;
    if (buyerId) {
      navigate(`/messages/${buyerId}`);
    }
  };

  const handleBuyNow = async () => {
    // Create purchase intent - no WhatsApp
    setSubmitting(true);
    try {
      await axios.post(`${API}/purchase-intents`, { product_id: productId }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("¡Solicitud enviada! El vendedor será notificado.");
      setBuyDialogOpen(false);
      navigate("/my-purchases"); // Go to my purchases to see the pending purchase
    } catch (error) {
      if (error.response?.data?.detail?.includes("pendiente")) {
        toast.info("Ya tienes una solicitud pendiente para este producto");
        navigate("/my-purchases");
      } else {
        toast.error(error.response?.data?.detail || "Error al enviar solicitud");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async () => {
    if (!user) {
      toast.error("Inicia sesión para pagar");
      navigate("/login");
      return;
    }
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        product_id: productId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar pago");
      setPaymentLoading(false);
    }
  };

  const BidContent = () => (
    <div className="space-y-6 p-4">
      <div className="text-center">
        <p className="text-sm text-[#64748B]">Oferta actual</p>
        <p className="text-3xl font-bold text-[#10B981]">${(product.current_bid || product.price).toLocaleString()}</p>
        {bidCount > 0 && (
          <p className="text-xs text-[#64748B] flex items-center justify-center gap-1 mt-1">
            <Users size={14} /> {bidCount} {bidCount === 1 ? "oferta" : "ofertas"}
          </p>
        )}
      </div>
      
      {/* Quick bid suggestions */}
      {bidSuggestions.length > 0 && (
        <div className="flex gap-2 justify-center" data-testid="bid-suggestions">
          {bidSuggestions.map((suggestion, idx) => (
            <button
              key={idx}
              onClick={() => setBidAmount(suggestion.amount)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                bidAmount === suggestion.amount
                  ? "bg-[#475569] text-white"
                  : "bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]"
              }`}
              data-testid={`bid-suggestion-${idx}`}
            >
              {suggestion.label}
            </button>
          ))}
        </div>
      )}
      
      <div className="bid-input-container">
        <button className="bid-button" onClick={() => setBidAmount(prev => Math.max(prev - 5, (product.current_bid || product.price) + 5))} data-testid="bid-decrease">
          <Minus size={20} />
        </button>
        <input type="number" value={bidAmount} onChange={(e) => setBidAmount(Number(e.target.value))} className="bid-input" data-testid="bid-input" />
        <button className="bid-button" onClick={() => setBidAmount(prev => prev + 5)} data-testid="bid-increase">
          <Plus size={20} />
        </button>
      </div>
      <p className="text-xs text-center text-[#64748B]">Incremento mínimo: $5</p>
      <Button onClick={handleBid} className="w-full rounded-full bg-[#475569] hover:bg-[#334155] h-12" disabled={submitting} data-testid="submit-bid">
        {submitting ? <div className="spinner" style={{ width: 20, height: 20 }} /> : "Realizar Oferta"}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Header />
        <div className="flex justify-center py-20"><div className="spinner"></div></div>
      </div>
    );
  }

  if (!product) return null;

  const isAuction = product.sale_type === "auction";
  const isOwner = user?.user_id === product.user_id;
  const canMakeOffer = !isAuction && offerCount.remaining > 0;
  
  // Check if product is scheduled (not yet published)
  const isScheduled = product.is_scheduled && product.scheduled_publish_date && new Date(product.scheduled_publish_date) > new Date();
  
  // Check if product is finalized (auction ended or sold)
  const auctionEnded = isAuction && product.auction_end_time && new Date(product.auction_end_time) < new Date();
  const isFinalized = product.is_sold || product.status === "sold";
  // Note: auctionEnded with status="reserved" means waiting for payment confirmation, not finalized
  
  // Check if user has this product reserved or has an active intent.
  // For auctions, a "reserved" (winner locked-in) status blocks EVERYONE (even guests) from bidding/buying-now.
  // For direct sale, only the specific buyer whose offer/intent was accepted sees it as reserved;
  // other buyers can still send new offers (only one reservation can be accepted at a time).
  const hasActiveReservation = (isAuction && product.status === "reserved") || !!(user && (
    product.buyer_id === user.user_id || 
    product.winner_id === user.user_id || 
    userPurchaseIntent || 
    userAcceptedOffer
  ));
  
  const canParticipate = !isOwner && !isFinalized && !isScheduled && !hasActiveReservation;

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />
      
      {/* Profile Completion Modal */}
      <ProfileCompletionModal
        open={profileModalOpen}
        onClose={() => { setProfileModalOpen(false); setPendingAction(null); }}
        user={user}
        token={token}
        API={API}
        onProfileComplete={handleProfileComplete}
      />

      <main className="max-w-6xl mx-auto px-4 py-2 md:py-3">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-1 md:mb-2" data-testid="back-button">
          <ArrowLeft size={20} />
          Volver
        </button>

        {/* Desktop: Side by side layout | Mobile: Stacked */}
        <div className="flex flex-col md:flex-row md:gap-6">
          {/* LEFT: Image Gallery */}
          <div className="md:w-1/2 md:sticky md:top-4 md:self-start">
          <div className="max-w-lg mx-auto md:max-w-none">
          {/* Image Gallery - Full Width */}
          <div className="space-y-1 md:space-y-2 mb-2 md:mb-0">
            <div className="relative rounded-2xl overflow-hidden bg-white shadow-sm">
              {/* Main Image */}
              {(() => {
                const allImages = [product.image_url, ...(product.images || [])].filter(Boolean);
                const currentImage = allImages[currentImageIndex] || product.image_url;
                return (
                  <>
                    <img src={currentImage} alt={product.title} onError={handleImgError} className="w-full aspect-square object-cover" data-testid="main-product-image" />
                    {/* Viewers pill - top left of image */}
                    <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-medium" data-testid="product-viewers-pill">
                      <Users size={12} />
                      <span>{product.cart_count > 0 ? product.cart_count + 6 : 8} viendo</span>
                      <span className="opacity-60">·</span>
                      <Heart size={11} />
                      <span>{product.likes_count}</span>
                    </div>
                    {/* Share via WhatsApp */}
                    <button
                      onClick={(e) => shareProductWhatsApp(product, e)}
                      className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                      data-testid="product-share-btn"
                      aria-label="Compartir por WhatsApp"
                    >
                      <Share2 size={16} />
                    </button>
                    {/* Navigation arrows */}
                    {allImages.length > 1 && (
                      <>
                        <button 
                          onClick={() => setCurrentImageIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                          data-testid="prev-image"
                        >
                          <ArrowLeft size={20} />
                        </button>
                        <button 
                          onClick={() => setCurrentImageIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors rotate-180"
                          data-testid="next-image"
                        >
                          <ArrowLeft size={20} />
                        </button>
                        {/* Image counter - moved up to not overlap thumbnails */}
                        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                          {currentImageIndex + 1} / {allImages.length}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
              {/* Thumbnail strip - Overlaid on main image */}
            {(() => {
              const allImages = [product.image_url, ...(product.images || [])].filter(Boolean);
              if (allImages.length <= 1) return null;
              return (
                <div className="absolute bottom-4 left-4 right-4 flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide" style={{WebkitOverflowScrolling: 'touch'}}>
                  {allImages.map((img, idx) => (
                    <button 
                      key={idx} 
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all shadow-md ${currentImageIndex === idx ? 'border-white ring-2 ring-white/50 scale-105' : 'border-white/50 opacity-80 hover:opacity-100 hover:border-white'}`}
                      data-testid={`thumbnail-${idx}`}
                    >
                      <img src={img} alt={`${product.title} ${idx + 1}`} onError={handleImgError} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              );
            })()}
            {isAuction && product.auction_end_time && !product.is_sold && (
                <div className="absolute top-4 right-4 bg-[#0F172A]/85 backdrop-blur text-white px-3 py-1.5 rounded-full flex items-center gap-2 text-sm">
                  <Clock size={14} />
                  <AuctionTimer endTime={product.auction_end_time} />
                </div>
              )}
            </div>
          </div>
          </div>
          </div>

          {/* RIGHT: Product Info */}
          <div className="md:w-1/2">
          <div className="max-w-lg mx-auto md:max-w-none">
          {/* Info - Single Column eBay Style */}
          <div className="space-y-4 md:space-y-5 px-1">
            {/* 1. Title + Like + Edit Button */}
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-xl md:text-2xl font-bold text-[#0F172A] leading-tight flex-1" data-testid="product-title">{product.title}</h1>
              <div className="flex items-center gap-2">
                {isOwner && (
                  <Link 
                    to={`/edit/${product.product_id}`}
                    className="flex-shrink-0 w-8 h-8 rounded-full border border-[#ddd] text-[#64748B] hover:border-[#0F172A] hover:text-[#0F172A] flex items-center justify-center transition-colors"
                    title="Editar producto"
                    data-testid="edit-product-btn"
                  >
                    <Pencil size={14} />
                  </Link>
                )}
                <button 
                  onClick={handleLike} 
                  className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${liked ? 'border-[#e53935] text-[#e53935] bg-[#e53935]/5' : 'border-[#ddd] text-[#64748B] hover:border-[#e53935] hover:text-[#e53935]'}`}
                  data-testid="detail-like"
                >
                  <Heart size={16} fill={liked ? "currentColor" : "none"} />
                </button>
              </div>
            </div>

            {/* 2. Seller Row: Avatar + Name + Rating | Message Button */}
            <div className="flex items-center justify-between py-2.5 border-b border-[#E2E8F0]">
              <div className="flex items-center gap-2">
                <Link to={`/profile/${product.user_id}`}>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={product.user_picture} alt={product.user_name} />
                    <AvatarFallback className="bg-[#475569] text-white text-xs">{product.user_name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                </Link>
                <div>
                  <Link to={`/profile/${product.user_id}`} className="font-semibold text-[#0F172A] hover:underline text-sm">
                    {product.user_name} ({sellerRating.review_count || 0})
                  </Link>
                  <div className="flex items-center gap-1 text-xs text-[#64748B] flex-wrap">
                    {sellerRating.avg_rating > 0 ? (
                      <>
                        <span className="text-[#F59E0B]">{'★'.repeat(Math.round(sellerRating.avg_rating))}</span>
                        <span>{(sellerRating.avg_rating * 20).toFixed(1)}% positivos</span>
                      </>
                    ) : (
                      <span>Nuevo vendedor</span>
                    )}
                    {sellerRating.sold_count > 0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium" data-testid="seller-sold-count">
                        <CheckCircle size={10} />
                        {sellerRating.sold_count} {sellerRating.sold_count === 1 ? "vendido" : "vendidos"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {!isOwner && (
                <Button variant="outline" className="rounded-full gap-1.5 h-9 px-4 text-sm font-semibold flex-shrink-0" onClick={handleMessage} data-testid="message-seller">
                  Mensaje
                </Button>
              )}
            </div>

            {/* 3. Price Section */}
            <div className={`transition-all duration-500 ${liveUpdateHighlight ? 'bg-[#10B981]/5' : ''}`}>
              <div className="flex items-baseline gap-3">
                <p className={`text-3xl font-bold transition-colors duration-300 ${liveUpdateHighlight ? 'text-[#059669]' : 'text-[#0F172A]'}`} data-testid="product-price">
                  ${(couponApplied?.final_price || product.promo_price || (isAuction ? (product.current_bid || product.price) : product.price))?.toLocaleString()}
                </p>
                {isAuction && product.auction_end_time && (
                  <div className="flex items-center gap-2 text-sm text-[#64748B]">
                    <span>|</span>
                    <span>{bidCount} {bidCount === 1 ? 'oferta' : 'ofertas'}</span>
                    <span>|</span>
                    <AuctionTimer endTime={product.auction_end_time} startTime={product.created_at} showHighlight={true} />
                  </div>
                )}
              </div>
              {product.promo_price && !couponApplied && (
                <div className="flex items-center gap-2 mt-1" data-testid="promo-discount-row">
                  <span className="text-sm line-through text-[#94A3B8]">${product.price?.toLocaleString()}</span>
                  <span className="text-xs font-bold bg-[#F97316]/10 text-[#EA580C] px-2 py-0.5 rounded-full">-{product.promo_discount_percent}% OFERTA</span>
                </div>
              )}
              {couponApplied && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm line-through text-[#94A3B8]">${product.price?.toLocaleString()}</span>
                  <span className="text-xs bg-[#10B981]/10 text-[#10B981] px-2 py-0.5 rounded-full">-{couponApplied.discount_percent}%</span>
                </div>
              )}
            </div>

            {/* 4. Action Buttons */}
            <div className="space-y-2.5 md:space-y-3 pt-2">
              {canParticipate && (
                <>
                  {/* AUCTION: Ofertar button first */}
                  {isAuction && (
                    <>
                      {/* Bid Confirmation Dialog - First time only */}
                      <Dialog open={bidConfirmationOpen} onOpenChange={setBidConfirmationOpen}>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Términos de la Subasta</DialogTitle>
                            <DialogDescription>Por favor acepta los términos antes de ofertar</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                              <Checkbox id="bid-terms" checked={bidTermsAccepted} onCheckedChange={setBidTermsAccepted} data-testid="bid-terms-checkbox" />
                              <label htmlFor="bid-terms" className="text-sm text-amber-800 cursor-pointer leading-tight">
                                Acepto que el precio ofertado <strong>NO ES NEGOCIABLE</strong> si resulto ganador en la subasta.
                              </label>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button onClick={handleBidTermsAccepted} disabled={!bidTermsAccepted} className="w-full rounded-full h-12 bg-[#475569] hover:bg-[#334155]" data-testid="accept-bid-terms">
                              Continuar a Ofertar
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      {/* Bid Button */}
                      {isMobile ? (
                        <Drawer open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
                          <Button 
                            onClick={handleBidClick} 
                            className={`w-full rounded-full h-11 text-sm ${!user ? 'bg-[#34D399] hover:bg-[#10B981]' : 'bg-[#0F172A] hover:bg-[#1e293b]'}`} 
                            data-testid="open-bid"
                          >
                            <Gavel size={16} className="mr-2" />
                            Participar en la Subasta
                          </Button>
                          <DrawerContent>
                            <DrawerHeader><DrawerTitle>Realizar Oferta</DrawerTitle></DrawerHeader>
                            <BidContent />
                          </DrawerContent>
                        </Drawer>
                      ) : (
                        <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
                          <Button 
                            onClick={handleBidClick} 
                            className={`w-full rounded-full h-11 text-sm ${!user ? 'bg-[#34D399] hover:bg-[#10B981]' : 'bg-[#0F172A] hover:bg-[#1e293b]'}`} 
                            data-testid="open-bid"
                          >
                            <Gavel size={16} className="mr-2" />
                            Participar en la Subasta
                          </Button>
                          <DialogContent>
                            <DialogHeader><DialogTitle>Realizar Oferta</DialogTitle></DialogHeader>
                            <BidContent />
                          </DialogContent>
                        </Dialog>
                      )}
                    </>
                  )}

                  {/* Section header for direct sales: adapts to accepts_offers flag */}
                  {!isAuction && (
                    <div className="flex items-center gap-2 pb-1">
                      <div className="h-px flex-1 bg-[#E2E8F0]" />
                      <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">
                        {product.accepts_offers === false ? "Precio Fijo" : "Compra o Mejor Oferta"}
                      </p>
                      <div className="h-px flex-1 bg-[#E2E8F0]" />
                    </div>
                  )}

                  {/* Comprar button - For auctions (buy_now_price) AND direct sales (product.price) */}
                  {((isAuction && product.buy_now_price) || !isAuction) && (() => {
                    const originalBuyPrice = isAuction ? product.buy_now_price : product.price;
                    const effectiveBuyPrice = couponApplied?.final_price ?? (isAuction ? null : product.promo_price) ?? originalBuyPrice;
                    return (
                    <Dialog open={buyDialogOpen} onOpenChange={setBuyDialogOpen}>
                      <div className="relative group">
                        <Button 
                          onClick={handleBuyNowClick} 
                          className={`w-full rounded-full h-11 text-sm pr-10 ${!user ? 'bg-[#34D399] hover:bg-[#10B981]' : 'bg-[#10B981] hover:bg-[#059669]'}`} 
                          data-testid="buy-now-button"
                        >
                          <ShoppingCart size={16} className="mr-2" />
                          {isAuction 
                            ? `Solicitar Compra sin Subasta - $${effectiveBuyPrice?.toLocaleString()}`
                            : `Comprar - $${effectiveBuyPrice?.toLocaleString()}`}
                        </Button>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 group">
                          <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-xs font-bold cursor-help" title="Compra el producto directamente. Revisas el producto en persona y luego lo pagas.">?</span>
                        </div>
                      </div>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle>{isAuction ? "Solicitar Compra Directa" : "Confirmar Compra"}</DialogTitle>
                          <DialogDescription>Estás solicitando comprar "{product.title}"{isAuction ? " sin participar en la subasta" : ""}</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="bg-[#F1F5F9] rounded-xl p-4 text-center">
                            <p className="text-sm text-[#64748B]">{isAuction ? "Precio de compra directa" : "Precio de compra"}</p>
                            <p className="text-3xl font-bold text-[#10B981]">${effectiveBuyPrice?.toLocaleString()}</p>
                            {couponApplied && effectiveBuyPrice !== originalBuyPrice && (
                              <p className="text-xs text-[#64748B] mt-1">
                                <span className="line-through">${originalBuyPrice?.toLocaleString()}</span>{" "}
                                <span className="text-[#10B981] font-semibold">-{couponApplied.discount_percent}% con cupón</span>
                              </p>
                            )}
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                            <p className="text-sm text-blue-800">
                              <strong>Pago contraentrega:</strong> Revisas el producto en persona y solo pagas si estás conforme. 
                              Si no cumple con lo descrito, puedes rechazarlo sin costo alguno.
                            </p>
                          </div>
                          <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <Checkbox id="price-accepted" checked={priceAccepted} onCheckedChange={setPriceAccepted} data-testid="price-checkbox" />
                            <label htmlFor="price-accepted" className="text-sm text-amber-800 cursor-pointer leading-tight">
                              Confirmo que deseo comprar este producto. Entiendo que el precio <strong>no es negociable</strong>.
                            </label>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button onClick={handleBuyNow} disabled={!priceAccepted || submitting} className="w-full rounded-full h-12 bg-[#10B981] hover:bg-[#059669] text-white" data-testid="buy-now-confirm-button">
                            {submitting ? <div className="spinner" style={{ width: 20, height: 20 }} /> : (
                              <>
                                <ShoppingCart size={18} className="mr-2" />
                                Enviar solicitud de compra
                              </>
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    );
                  })()}

                  {/* Enviar una oferta - For direct sale products (MAIN BUTTON) */}
                  {/* Hidden when product is marked as fixed-price (accepts_offers === false) */}
                  {!isAuction && product.accepts_offers !== false && (
                    <Dialog open={offerDialogOpen} onOpenChange={(open) => {
                      setOfferDialogOpen(open);
                      if (!open) {
                        setOfferTermsAccepted(false);
                      }
                    }}>
                      <div className="relative">
                        <Button 
                          onClick={handleOfferClick} 
                          className={`w-full rounded-full h-11 text-sm pr-10 ${!user ? 'bg-[#34D399] hover:bg-[#10B981]' : 'bg-[#0F172A] hover:bg-[#1e293b]'}`} 
                          disabled={user && !canMakeOffer && !offerCount.has_pending_counter} 
                          data-testid="make-offer-button"
                        >
                          <Tag size={18} className="mr-2" />
                          {!user 
                            ? "Último precio?"
                            : offerCount.has_pending_counter 
                              ? `Contraoferta: $${offerCount.last_counter_amount}` 
                              : canMakeOffer 
                                ? `Último precio? (${offerCount.remaining} restantes)` 
                                : "Límite de ofertas alcanzado"}
                        </Button>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-xs font-bold cursor-help" title="Pregunta al vendedor por su último precio enviando tu mejor oferta. El vendedor puede aceptar, rechazar o contraproponer.">?</span>
                        </div>
                      </div>
                      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>
                            {offerCount.has_pending_counter ? "Responder a Contraoferta" : "Hacer una Oferta"}
                          </DialogTitle>
                          <DialogDescription>
                            {offerCount.has_pending_counter 
                              ? `El vendedor te envió una contraoferta de $${offerCount.last_counter_amount}. Puedes aceptarla, rechazarla o hacer una nueva oferta.`
                              : `Envía tu mejor oferta al vendedor. Te quedan ${offerCount.remaining} ofertas.`}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          {/* Counter offer alert */}
                          {offerCount.has_pending_counter && (
                            <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl text-center">
                              <p className="text-sm text-purple-600 font-medium">Contraoferta del vendedor</p>
                              <p className="text-3xl font-bold text-purple-700">${offerCount.last_counter_amount?.toLocaleString()}</p>
                              <div className="flex gap-2 mt-3">
                                <Button 
                                  size="sm" 
                                  onClick={handleAcceptCounterOffer} 
                                  disabled={submitting}
                                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                                  data-testid="accept-counter-btn"
                                >
                                  <Check size={16} className="mr-1" /> Aceptar
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={handleRejectCounterOffer} 
                                  disabled={submitting}
                                  className="flex-1 border-red-500 text-red-500 hover:bg-red-50"
                                  data-testid="reject-counter-btn"
                                >
                                  <X size={16} className="mr-1" /> Rechazar
                                </Button>
                              </div>
                            </div>
                          )}
                          
                          <div className="text-center">
                            <p className="text-sm text-[#64748B]">Precio original</p>
                            <p className="text-2xl font-bold text-[#0F172A]">${product.price?.toLocaleString()}</p>
                          </div>
                          
                          {/* Quick suggestion buttons */}
                          <div className="flex gap-2 justify-center">
                            <button
                              type="button"
                              onClick={() => setOfferAmount(Math.round(product.price * 0.95))}
                              className="px-3 py-1.5 text-xs bg-amber-100 text-amber-700 rounded-full hover:bg-amber-200 transition-colors"
                            >
                              💡 -5% (${Math.round(product.price * 0.95)})
                            </button>
                            <button
                              type="button"
                              onClick={() => setOfferAmount(Math.round(product.price * 0.90))}
                              className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                            >
                              -10% (${Math.round(product.price * 0.90)})
                            </button>
                          </div>
                          
                          {/* Only show new offer input if user has remaining offers */}
                          {offerCount.remaining > 0 && (
                            <>
                              <div className="border-t pt-4">
                                <label className="text-sm font-medium text-[#0F172A]">
                                  {offerCount.has_pending_counter ? "Tu nueva oferta" : "Tu oferta"}
                                </label>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-2xl font-bold text-[#10B981]">$</span>
                                  <Input 
                                    type="text" 
                                    inputMode="numeric"
                                    value={offerAmount || ""} 
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9]/g, '');
                                      setOfferAmount(val ? parseInt(val) : 0);
                                    }} 
                                    className="text-2xl font-bold h-14" 
                                    placeholder="0"
                                    data-testid="offer-input" 
                                  />
                                </div>
                                <p className="text-xs text-[#64748B] mt-2">
                                  {offerAmount > 0 && offerAmount < product.price ? `${Math.round((1 - offerAmount / product.price) * 100)}% menos que el precio original` : offerAmount >= product.price ? "Tu oferta es igual o mayor al precio original" : "Ingresa un monto"}
                                </p>
                                <p className="text-xs text-amber-600 mt-1">
                                  Te quedan {offerCount.remaining} {offerCount.remaining === 1 ? 'oferta' : 'ofertas'}
                                </p>
                              </div>
                              
                              {/* Terms checkbox */}
                              <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                <Checkbox 
                                  id="offer-terms" 
                                  checked={offerTermsAccepted} 
                                  onCheckedChange={setOfferTermsAccepted} 
                                  data-testid="offer-terms-checkbox" 
                                />
                                <label htmlFor="offer-terms" className="text-sm text-amber-800 cursor-pointer leading-tight">
                                  Acepto que si mi oferta es aceptada, el precio <strong>no es negociable</strong> y me comprometo a completar la compra.
                                </label>
                              </div>
                            </>
                          )}
                          
                          {/* No more offers message */}
                          {offerCount.remaining === 0 && !offerCount.has_pending_counter && (
                            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-center">
                              <p className="text-sm text-gray-600">
                                Has usado tus 3 ofertas. Puedes comprar al precio original de ${product.price?.toLocaleString()}.
                              </p>
                            </div>
                          )}
                        </div>
                        <DialogFooter className="flex-col gap-2">
                          {offerCount.remaining > 0 && (
                            <Button 
                              onClick={handleSendOffer} 
                              disabled={submitting || offerAmount <= 0 || !offerTermsAccepted} 
                              className="w-full rounded-full h-12 bg-[#475569] hover:bg-[#334155]" 
                              data-testid="submit-offer"
                            >
                              {submitting ? <div className="spinner" style={{ width: 20, height: 20 }} /> : (
                                offerCount.has_pending_counter ? "Enviar Nueva Oferta" : "Enviar Oferta"
                              )}
                            </Button>
                          )}
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {/* Pago contraentrega link - directly after action buttons */}
                  <button 
                    onClick={() => setPaymentInfoOpen(true)}
                    className="flex items-center justify-center gap-2 w-full text-center hover:bg-[#FEF3C7]/50 rounded-lg py-2 transition-colors"
                    data-testid="payment-info-btn"
                  >
                    <Shield size={14} className="text-[#D97706]" />
                    <p className="text-xs text-[#D97706] font-medium underline decoration-dotted underline-offset-2">
                      Pago contraentrega: Revisas el producto y luego lo pagas →
                    </p>
                  </button>
                </>
              )}

              {/* Coupon input for non-auction products with discount */}
              {!isAuction && product.has_discount && !couponApplied && !isOwner && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
                  <p className="text-sm text-purple-700 font-medium mb-2">¿Tienes un código de descuento?</p>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Ingresa tu código" 
                      value={couponCode} 
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1"
                      data-testid="coupon-input"
                    />
                    <Button 
                      onClick={handleApplyCoupon} 
                      disabled={couponLoading || !couponCode.trim()}
                      variant="outline"
                      className="border-purple-500 text-purple-600 hover:bg-purple-100"
                      data-testid="apply-coupon-btn"
                    >
                      {couponLoading ? "..." : "Aplicar"}
                    </Button>
                  </div>
                </div>
              )}
              {couponApplied && (
                <div className="p-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  ✓ Cupón aplicado: {couponApplied.discount_percent}% de descuento
                </div>
              )}

              {/* Scheduled product message */}
              {isScheduled && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-center" data-testid="scheduled-notice">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Clock size={20} className="text-amber-600" />
                    <p className="font-semibold text-amber-800">Próximamente</p>
                  </div>
                  <p className="text-sm text-amber-700 mb-3">Este producto aún no está disponible para compra</p>
                  <div className="inline-block bg-white px-4 py-2 rounded-full">
                    <ScheduledTimer publishDate={product.scheduled_publish_date} />
                  </div>
                </div>
              )}

              {/* Show auction winner message when auction ended and user is winner */}
              {auctionEnded && product.status === "reserved" && product.winner_id === user?.user_id && !product.is_sold && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center">
                  <p className="font-bold text-green-700 text-lg">🎉 ¡Ganaste la Subasta!</p>
                  <p className="text-sm text-green-600 mt-1">Contacta al vendedor para coordinar el pago y entrega</p>
                </div>
              )}

              {/* Show auction ended message for non-winners */}
              {auctionEnded && product.status === "reserved" && product.winner_id !== user?.user_id && !product.is_sold && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
                  <p className="font-semibold text-amber-800">Subasta Finalizada</p>
                  <p className="text-sm text-amber-700">Esta subasta ha terminado y tiene un ganador</p>
                </div>
              )}

              {isFinalized && product.is_sold && (
                <div className="p-4 bg-gray-100 rounded-xl text-center">
                  <p className="font-semibold text-gray-700">Producto Vendido</p>
                </div>
              )}

              {/* Show when product is sold OR reserved for current user OR user has active intent */}
              {(product.is_sold || 
                (product.status === "reserved" && (product.buyer_id === user?.user_id || product.winner_id === user?.user_id)) ||
                userPurchaseIntent || userAcceptedOffer) && (
                <div className="space-y-3">
                  {/* Status badge - different for sold vs reserved vs pending */}
                  {/* Only show "Reservado para ti" or "Pagado" if user is the buyer/winner */}
                  {(product.status === "reserved" || userPurchaseIntent?.status === "reserved" || userAcceptedOffer) && !product.is_sold && 
                   (product.buyer_id === user?.user_id || product.winner_id === user?.user_id || userPurchaseIntent || userAcceptedOffer) ? (
                    <span className="block w-full text-center py-3 bg-[#10B981]/10 rounded-full text-[#10B981] font-semibold" data-testid="reserved-badge">
                      Reservado para ti
                    </span>
                  ) : userPurchaseIntent?.status === "pending" ? (
                    <span className="block w-full text-center py-3 bg-amber-50 border border-amber-200 rounded-full text-amber-700 font-semibold" data-testid="pending-badge">
                      Solicitud pendiente
                    </span>
                  ) : product.is_sold && (product.buyer_id === user?.user_id || product.winner_id === user?.user_id) && product.paid ? (
                    <span className="block w-full text-center py-3 bg-[#3B82F6]/10 rounded-full text-[#3B82F6] font-semibold" data-testid="paid-badge">
                      Pagado
                    </span>
                  ) : product.is_sold ? (
                    <span className="block w-full text-center py-3 bg-[#F1F5F9] rounded-full text-[#64748B] font-semibold" data-testid="sold-badge">
                      Vendido
                    </span>
                  ) : null}
                  
                  {/* Contact buttons for winner/buyer/accepted user - show when reserved but NOT paid */}
                  {user && (product.winner_id === user.user_id || product.buyer_id === user.user_id || userAcceptedOffer || userPurchaseIntent?.status === "reserved") && !product.paid && (
                    <>
                      {/* Internal message button - Contactar al Vendedor */}
                      <Button 
                        onClick={() => navigate(`/messages/${product.user_id}`)} 
                        variant="outline" 
                        className="w-full rounded-full h-12 border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10" 
                        data-testid="contact-seller-btn"
                      >
                        <MessageCircle size={18} className="mr-2" />
                        Contactar al Vendedor
                      </Button>
                      
                      {/* WhatsApp button */}
                      <a
                        href={`https://wa.me/50712345678?text=${encodeURIComponent(`Hola, gané/compré "${product.title}" y quisiera coordinar la entrega.`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Button className="w-full rounded-full h-12 bg-[#34D399] hover:bg-[#10B981] text-white" data-testid="winner-whatsapp-button">
                          <MessageSquare size={18} className="mr-2" />
                          Mensaje por WhatsApp
                        </Button>
                      </a>
                    </>
                  )}
                  
                  {/* Review button only when paid */}
                  {user && (product.winner_id === user.user_id || product.buyer_id === user.user_id) && product.paid && (
                    <Link to={`/review/${productId}`} className="block">
                      <Button variant="outline" className="w-full rounded-full h-12" data-testid="review-button"><Star size={18} className="mr-2" />Dejar Reseña</Button>
                    </Link>
                  )}
                </div>
              )}
              
              {/* Admin: Message buyer/winner button */}
              {isOwner && (product.buyer_id || product.winner_id) && (
                <Button 
                  onClick={handleMessageBuyer} 
                  variant="outline" 
                  className="w-full rounded-full h-12 border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10"
                  data-testid="message-buyer-btn"
                >
                  <MessageCircle size={18} className="mr-2" />
                  Mensaje al {product.winner_id ? "Ganador" : "Comprador"}
                </Button>
              )}
            </div>

            {/* Payment Info Modal */}
            <Dialog open={paymentInfoOpen} onOpenChange={setPaymentInfoOpen}>
              <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-[#0F172A]">
                    <Shield size={20} className="text-[#D97706]" />
                    Proceso de Compra Segura
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2 text-sm">
                  {/* Item 1 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#10B981] text-white flex items-center justify-center text-xs font-bold">1</div>
                    <p className="text-[#374151]">
                      <strong>Clueer no solicita pagos anticipados</strong> dentro de su marketplace a ningún usuario ni la implementación de datos bancarios, tarjetas o información sensible del mismo.
                    </p>
                  </div>

                  {/* Item 2 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#10B981] text-white flex items-center justify-center text-xs font-bold">2</div>
                    <div className="text-[#374151]">
                      <p>Una vez sea ganador de su producto coordinará con el vendedor la <strong>inspección física</strong> de su producto el cual debe concordar con el anuncio un 100%. De no ser así, no está obligado a pagarlo.</p>
                      <p className="mt-2">Si está conforme con el producto podrá emitir su pago vía:</p>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center gap-1 bg-[#00D1A1]/10 px-3 py-1.5 rounded-full">
                          <span className="text-[#00D1A1] font-bold text-xs">Yappy</span>
                        </div>
                        <span className="text-[#64748B]">o</span>
                        <div className="flex items-center gap-1 bg-[#003366]/10 px-3 py-1.5 rounded-full">
                          <span className="text-[#003366] font-semibold text-xs">BAC Panamá</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Item 3 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#F59E0B] text-white flex items-center justify-center text-xs font-bold">3</div>
                    <div className="text-[#374151]">
                      <p><strong>Envíos:</strong> Si desea un envío, el pago del producto deberá emitirse contra la prueba de envío (videollamada, fotos y documentación de la empresa de envío).</p>
                      <p className="mt-1 text-xs text-[#64748B] bg-[#FEF3C7] p-2 rounded">
                        <AlertTriangle size={12} className="inline mr-1" />
                        Siempre recomendamos evaluar la alternativa de un tercero que pueda retirar el producto físicamente.
                      </p>
                    </div>
                  </div>

                  {/* Item 4 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#EF4444] text-white flex items-center justify-center text-xs font-bold">4</div>
                    <p className="text-[#374151]">
                      El precio del producto una vez acordado y/o ganado por el usuario <strong className="text-[#EF4444]">NO ES NEGOCIABLE</strong>. Deberá pagar el total aplicado en el sitio web.
                    </p>
                  </div>

                  {/* Item 5 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#64748B] text-white flex items-center justify-center text-xs font-bold">5</div>
                    <p className="text-[#374151]">
                      Una vez sea entregado un producto al usuario y el mismo dé fe de su conformidad, <strong>no hay devoluciones o reclamos</strong> que apliquen a este tipo de gestión de compraventa.
                    </p>
                  </div>

                  {/* Item 6 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#64748B] text-white flex items-center justify-center text-xs font-bold">6</div>
                    <p className="text-[#374151]">
                      <strong>No se ofrecen garantías</strong> por los equipos vendidos. Son en su gran mayoría equipos con detalles estéticos, semifuncionales o funcionales que son bien detallados en la descripción y fotografías para que cada usuario evalúe a su propio criterio.
                    </p>
                  </div>

                  {/* Item 7 */}
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#10B981] text-white flex items-center justify-center text-xs font-bold">
                      <Gift size={12} />
                    </div>
                    <div className="text-[#374151]">
                      <p><strong className="text-[#10B981]">Recompensas:</strong> Se ofrecen recompensas por comentarios de su experiencia con el vendedor, la plataforma y el producto.</p>
                      <p className="mt-1 text-xs text-[#64748B]">Las recompensas son únicas y aplican solamente para usuarios que hayan realizado una compra. Se pagan vía Yappy al número designado en su formulario.</p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="pt-4 border-t border-[#E2E8F0] text-center">
                    <p className="font-bold text-[#0F172A]">Clueer | Marketplace de Subastas 🇵🇦</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => setPaymentInfoOpen(false)} className="w-full rounded-full bg-[#0F172A] hover:bg-[#1e293b]">
                    Entendido
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* 6. Description - highlighted block */}
            <div className="mt-2 bg-[#FFFBEB] border border-[#FDE68A] rounded-2xl p-4 md:p-5" data-testid="product-description-block">
              <h3 className="font-bold text-[#0F172A] text-lg md:text-xl mb-2">Descripción</h3>
              <p className="text-sm text-[#475569] leading-relaxed" data-testid="product-description">{product.description}</p>
            </div>

            {/* Ficha técnica - only shown when the admin filled at least one field */}
            {(product.condition_grade || product.storage_ram || product.included_accessories || product.warranty_info) && (
              <div className="mt-4 rounded-2xl border border-[#E2E8F0] overflow-hidden" data-testid="product-ficha-tecnica">
                <div className="bg-[#F8FAFC] px-4 py-2.5 border-b border-[#E2E8F0]">
                  <p className="text-xs font-bold text-[#0F172A] uppercase tracking-wide">Ficha técnica</p>
                </div>
                <div className="divide-y divide-[#F1F5F9]">
                  {[
                    { label: "Condición", value: product.condition_grade },
                    { label: "Almacenamiento / RAM", value: product.storage_ram },
                    { label: "Accesorios incluidos", value: product.included_accessories },
                    { label: "Garantía", value: product.warranty_info },
                  ].filter((f) => f.value).map(({ label, value }) => (
                    <div key={label} className="flex items-start justify-between gap-3 px-4 py-2.5">
                      <p className="text-sm text-[#64748B]">{label}</p>
                      <p className="text-sm font-medium text-[#0F172A] text-right">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Seller Rating - Only shown on sold products (already visible at top for active) */}
            {isFinalized && sellerRating.review_count > 0 && (
              <div className="flex items-center gap-2 py-4 md:py-5 border-t border-[#E2E8F0]">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} size={16} className={star <= Math.round(sellerRating.avg_rating) ? "fill-[#FBBF24] text-[#FBBF24]" : "text-[#E2E8F0]"} />
                  ))}
                </div>
                <span className="font-semibold text-[#0F172A]">{sellerRating.avg_rating.toFixed(1)}</span>
                <span className="text-sm text-[#64748B]">({sellerRating.review_count} reseñas)</span>
              </div>
            )}
          </div>
          </div>
          </div>
        </div>
        {/* End of side-by-side layout */}

        {/* Reviews Section - Only shown for sold products */}
        {isFinalized && reviews.length > 0 && (
          <div className="mt-4 md:mt-8 max-w-2xl mx-auto md:max-w-4xl">
            <h3 className="font-semibold text-[#0F172A] mb-2 md:mb-4 text-lg px-1">Reseñas del producto</h3>
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.review_id} className="bg-white rounded-2xl p-4 border border-[#E2E8F0]" data-testid={`review-${review.review_id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={review.reviewer_picture} alt={review.reviewer_name} />
                      <AvatarFallback className="bg-[#475569] text-white text-sm">{review.reviewer_name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-semibold text-[#0F172A]">{review.reviewer_name}</p>
                        {review.reviewer_auth_method === "whatsapp" && <WhatsAppBadge />}
                      </div>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} size={14} className={star <= review.rating ? "fill-[#FBBF24] text-[#FBBF24]" : "text-[#E2E8F0]"} />
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#64748B]">{formatDistanceToNow(new Date(review.created_at), { addSuffix: true, locale: es })}</span>
                      {user?.is_admin && (
                        <button
                          onClick={() => handleDeleteReview(review.review_id)}
                          className="p-1 text-[#EF4444] hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar reseña"
                          data-testid={`delete-review-${review.review_id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-[#0F172A]">{review.comment}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommended Products - replaces comments section */}
        {recommended.length > 0 && (
          <div className="mt-6 md:mt-12 max-w-2xl mx-auto md:max-w-4xl px-1" data-testid="recommended-products">
            <h3 className="font-bold text-[#0F172A] mb-3 md:mb-4 text-lg">Productos recomendados</h3>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-3" style={{ WebkitOverflowScrolling: "touch" }}>
              {recommended.map((p) => (
                <div key={p.product_id} className="flex-shrink-0 w-40">
                  <ProductCard product={p} compact={true} horizontal={true} />
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <BottomNav />
      
      {/* Quick Offer Popup for non-logged users */}
      <QuickOfferPopup
        isOpen={quickOfferOpen}
        onClose={() => setQuickOfferOpen(false)}
        product={product}
        onSuccess={handleQuickOfferSuccess}
        mode={quickOfferMode}
      />
    </div>
  );
};

export default ProductDetailPage;
